/**
 * Responsive Design System
 * Professional design utilities for multi-device optimization
 */

// ============== COLOR PALETTE ==============
export const colors = {
  // Primary (Teal)
  primary: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },
  // Secondary (Blue)
  secondary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Success (Green)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#145231',
  },
  // Warning (Amber)
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  // Danger (Red)
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  // Neutral (Gray)
  neutral: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
};

// ============== SPACING SCALE ==============
export const spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '2.5rem',  // 40px
  '3xl': '3rem',    // 48px
  '4xl': '4rem',    // 64px
};

// ============== TYPOGRAPHY ==============
export const typography = {
  // Headings
  h1: 'text-4xl md:text-5xl lg:text-6xl font-bold',
  h2: 'text-3xl md:text-4xl lg:text-5xl font-bold',
  h3: 'text-2xl md:text-3xl lg:text-4xl font-bold',
  h4: 'text-xl md:text-2xl font-bold',
  h5: 'text-lg md:text-xl font-bold',
  h6: 'text-base md:text-lg font-bold',
  
  // Body
  body: 'text-base md:text-lg',
  bodySmall: 'text-sm md:text-base',
  bodyXSmall: 'text-xs md:text-sm',
  
  // Emphasis
  bold: 'font-bold',
  semibold: 'font-semibold',
  medium: 'font-medium',
  regular: 'font-normal',
};

// ============== RESPONSIVE BREAKPOINTS ==============
export const breakpoints = {
  xs: '320px',   // Mobile small
  sm: '640px',   // Mobile
  md: '768px',   // Tablet
  lg: '1024px',  // Desktop
  xl: '1280px',  // Desktop large
  '2xl': '1536px', // Desktop extra large
};

// ============== RESPONSIVE UTILITIES ==============
export const responsive = {
  // Container padding
  containerPadding: 'px-4 sm:px-6 md:px-8 lg:px-10',
  
  // Grid columns
  grid2Col: 'grid-cols-1 sm:grid-cols-2',
  grid3Col: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  grid4Col: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  grid2ColMd: 'grid-cols-1 md:grid-cols-2',
  grid3ColMd: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  
  // Card layouts
  cardGap: 'gap-3 sm:gap-4 md:gap-6',
  cardPadding: 'p-4 sm:p-6 md:p-8',
  
  // Text sizing
  textBase: 'text-xs sm:text-sm md:text-base lg:text-lg',
  textLarge: 'text-sm sm:text-base md:text-lg lg:text-xl',
  textXLarge: 'text-base sm:text-lg md:text-xl lg:text-2xl',
  
  // Flex stacking
  flexCol: 'flex flex-col md:flex-row',
  flexColLg: 'flex flex-col lg:flex-row',
};

// ============== COMPONENT STYLES ==============
export const componentStyles = {
  // Cards
  card: 'bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200',
  cardNested: 'bg-gray-50 rounded border border-gray-100',
  
  // Buttons
  buttonPrimary: 'px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
  buttonSecondary: 'px-4 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200',
  buttonDanger: 'px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200',
  
  // Inputs
  input: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
  inputSmall: 'px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500',
  
  // Badge/Pills
  badge: 'inline-block px-3 py-1 text-xs font-semibold rounded-full',
  badgePrimary: 'bg-teal-100 text-teal-800',
  badgeGreen: 'bg-green-100 text-green-800',
  badgeRed: 'bg-red-100 text-red-800',
  badgeYellow: 'bg-yellow-100 text-yellow-800',
  badgeGray: 'bg-gray-100 text-gray-800',
  
  // Tables
  tableHeader: 'bg-gray-50 border-b border-gray-200',
  tableRow: 'border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200',
  
  // Sections
  section: 'py-8 md:py-12 lg:py-16',
  sectionSmall: 'py-4 md:py-6 lg:py-8',
};

// ============== RESPONSIVE LAYOUTS ==============
export const layouts = {
  // Page layouts
  pageContainer: 'min-h-screen bg-gray-50',
  pageContent: 'max-w-7xl mx-auto ' + responsive.containerPadding,
  
  // Header layouts
  headerDefault: 'flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6 mb-6 md:mb-8',
  headerCompact: 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4 sm:mb-6',
  
  // Two column layout (sidebar + content)
  sidebarLayout: 'grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8',
  sidebarWidget: 'lg:col-span-1',
  mainContent: 'lg:col-span-3',
};

// ============== PROFESSIONAL SHADOWS ==============
export const shadows = {
  light: 'shadow-sm',
  medium: 'shadow-md',
  heavy: 'shadow-lg',
  hover: 'hover:shadow-md transition-shadow',
};

// ============== TRANSITIONS ==============
export const transitions = {
  fast: 'transition-all duration-150',
  normal: 'transition-all duration-200',
  slow: 'transition-all duration-300',
};

// ============== HELPER FUNCTIONS ==============
/**
 * Combine multiple class strings conditionally
 */
export const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Get responsive class for grid
 */
export const getGridClass = (columns: 2 | 3 | 4, breakpoint: 'default' | 'md' = 'default'): string => {
  const gridMap = {
    2: responsive.grid2Col,
    3: responsive.grid3Col,
    4: responsive.grid4Col,
  };
  return gridMap[columns];
};

/**
 * Get responsive font size
 */
export const getResponsiveText = (size: 'sm' | 'base' | 'lg' | 'xl'): string => {
  const textMap = {
    sm: responsive.textBase,
    base: responsive.textLarge,
    lg: responsive.textXLarge,
    xl: 'text-lg sm:text-xl md:text-2xl lg:text-3xl',
  };
  return textMap[size];
};

export default {
  colors,
  spacing,
  typography,
  breakpoints,
  responsive,
  componentStyles,
  layouts,
  shadows,
  transitions,
  classNames,
  getGridClass,
  getResponsiveText,
};
